import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "business-detail" }
const _hoisted_2 = { class: "business-detail__container" }
const _hoisted_3 = { class: "business-detail__container--left-content" }
const _hoisted_4 = { class: "business-detail__container--right-content" }

import { onMounted, onUnmounted, provide, ref } from 'vue';

import { i18n } from '@/i18n';
import OrderForm from '@/recommendOrderModule/components/OrderForm.vue';
import RecommendEdit from '@/recommendOrderModule/pages/RecommendEdit.vue';
import { useRoute, useRouter } from 'vue-router';
import { HeaderHeight, contentMargin, removePxString } from '@/appLogic/constants/layoutConst';
import { key, useRecommendOrderStore } from './useRecommendOrderStore';
import { BusinessService } from '../domains/services/businessService';
import { RecommendOrderArgs } from './interfaces/recommendOrder';
import { openConfirmModal, openOkInformationModal, openFailToAcquireModal } from '@/corePagesModule/components/modal/modalActions';
import { useSelectedBusinessStore } from './selectedBusinessStore';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';
import { simpleCopy } from '@/utils/deepCopy';
import { useAggregateBusinessIdHasRecommendOrderStore } from '@/corePagesModule/store/useAggregateBusinessIdHasRecommendOrderStore';
import { diContainer } from '@/coreContainer/container';
import EditUnpaidOrderedOrderModal from '@/corePagesModule/components/EditUnpaidOrderedOrderModal.vue';
import { SimulationSetting } from '@/coreDomainsModule/simulation';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';


export default /*@__PURE__*/_defineComponent({
  __name: 'main',
  emits: ["submit", "cancel"],
  setup(__props, { emit: __emit }) {

_useCssVars(_ctx => ({
  "318d4cc5": (`${_unref(HeaderHeight)} - ${_unref(contentMarginRawValue) * 2}px`)
}))

const emits = __emit;

//変数
const t = i18n.t;
const route = useRoute();
const router = useRouter();
const bizId = route.params.id;
const pathName = route.name;

const args: RecommendOrderArgs = {
  businessId: bizId as string,
};

let store = useRecommendOrderStore(args);
const simulationSetting = ref<SimulationSetting>({
  useRoundDown: false,
  useCurrentMonthSalesResult: false,
});
const selectedBusinessStore = useSelectedBusinessStore();
const { state } = selectedBusinessStore;
const aggregateBusinessIdsHasRecommendOrderStore = useAggregateBusinessIdHasRecommendOrderStore();
const { state: aggregateBusinessIdsHasRecommendOrderState } = aggregateBusinessIdsHasRecommendOrderStore;
// style variables
const contentMarginRawValue = removePxString(contentMargin);

// イベントフック
onMounted(async () => {
  store.state.loadingState = {
    status: {
      type: 'loading',
    },
  };

  // 推奨一括発注の場合、localStorageから集計IDリストを取得し、stateに保存する
  if (pathName === 'recommend-order-with-list') {
    loadAggregateBusinessIdsForPaging();
  }

  const settingResult = await diContainer.getSimulationSettingUseCase.execute();
  if (settingResult.status.type === 'success') {
    simulationSetting.value = {
      useRoundDown: settingResult.status.data.useRoundDown,
      useCurrentMonthSalesResult: settingResult.status.data.useCurrentMonthSalesResult,
    };
  } else {
    showInfoModal({
      modalTitle: t('シミュレーション設定の取得に失敗しました'),
      modalDescription: t('デフォルト設定（全て無効）でシミュレーションを行います'),
      nextButtonLabel: t('OK'),
    });
  }

  const copyItems = await diContainer.getBusinessUseCase.Execute(bizId as string);

  switch (copyItems.status.type) {
    case 'success':
      // 内示情報を過去表示用のstate変数に保存
      store.state.customerForecastHistory = simpleCopy(copyItems.status.data.customerAnnounce ?? []);

      // 取引情報の取得
      store.state.formSet = BusinessService.convertRecommendEditBusiness(copyItems.status.data);

      // フィルターを埋める
      store.updateSelectedInfo(
        store.state.formSet.warehouse,
        store.state.formSet.supplierName,
        store.state.formSet.businessNumber,
        store.state.formSet.businessName,
        store.state.formSet.package
      );

      // フィルターの初期値を設定
      store.state.selectedInitialInfo = simpleCopy(store.state.selectedInfo);

      // 発注済み未納を取得
      store.state.unpaidOrderedOrder = BusinessService.getUnpaidOrderedOrder(copyItems.status.data);

      // storeの初期化
      store.initState();

      // inventoryがundefinedの場合(有効な当月~3ヶ月前の在庫が存在しない場合)ここで失敗として扱いエラーモーダルを表示する
      if (copyItems.status.data.inventory == null) {
        console.error('invalid inventory error');
        store.state.loadingState = {
          status: {
            type: 'error',
            errorMessage: t('無効な在庫エラーメッセージ(エラーモーダル用)'),
          },
        };
        store.state.errorType = 'INVALID_INVENTORY';
        openOkInformationModal('error', t('エラー'), 'OK', undefined, t('無効な在庫エラーメッセージ(エラーモーダル用)'));
        return;
      }

      // 確定発注が存在する場合、最終確定日の翌日の日付を取得し、extraSimulateを更新する
      const lockedRecommend = store.state.recommend.filter((item) => item.isLocked || item.arrivalId != '') ?? [];
      const lockedRecommendLast = lockedRecommend?.[lockedRecommend?.length - 1];

      const startDate = store.calculateStartDate(lockedRecommendLast);
      if (startDate) {
        store.updateExtraSimulate(undefined, undefined, undefined, lockedRecommend, startDate);
      }

      store.state.renderOrderFormKey += 1;

      // 長期客先内示データを取得
      const result = await diContainer.getLongTermCustomerForecastUseCase.execute(bizId as string);
      switch (result.status.type) {
        case 'success':
          store.state.longTermCustomerForecastData = result.status.data;

          // --- データ統合処理 ---
          // 1. 長期内示の形式を短期内示の形式に変換
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const normalizedLongTermData = (store.state.longTermCustomerForecastData?.data ?? []).map((item: any) => ({
            year: item.customerForecastTargetYear,
            month: item.customerForecastTargetMonth,
            amount: item.customerForecastAmount,
            manageInitiatedYearMonth: {
              year: item.customerForecastReceivedYear,
              month: item.customerForecastReceivedMonth,
            },
          }));

          // 2. 短期内示(既存のcustomerForecastHistory)と正規化した長期内示を合体
          const allForecastData = [...store.state.customerForecastHistory, ...normalizedLongTermData];

          // 3. 重複データを削除 (target-YYYYMM と received-YYYYMM でユニーク化)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const uniqueForecasts = new Map<string, any>();
          allForecastData.forEach((forecast) => {
            if (forecast.manageInitiatedYearMonth) {
              const targetKey = `${forecast.year}${forecast.month.toString().padStart(2, '0')}`;
              const receivedKey = `${forecast.manageInitiatedYearMonth.year}${forecast.manageInitiatedYearMonth.month
                .toString()
                .padStart(2, '0')}`;
              const uniqueKey = `${targetKey}-${receivedKey}`;
              uniqueForecasts.set(uniqueKey, forecast);
            }
          });

          // 4. 統合した完全なデータで customerForecastHistory を上書き
          store.state.customerForecastHistory = Array.from(uniqueForecasts.values());
          break;
        case 'error':
          console.error('getLongTermCustomerForecastUseCase.execute error:', result.status.error);
          openFailToAcquireModal();
          break;
      }

      store.state.loadingState = {
        status: {
          type: 'success',
        },
      };
      break;
    case 'error':
      console.error('getBusinessUseCase.Execute error');
      store.state.loadingState = {
        status: {
          type: 'error',
          errorMessage: t('API取得エラーメッセージ(エラーモーダル用)'),
        },
      };
      store.state.errorType = 'API_ERROR';
      openOkInformationModal('error', t('エラー'), 'OK', t('API取得エラーメッセージ(エラーモーダル用)'));
      break;
  }

  window.addEventListener('popstate', handlePopState);
});

onUnmounted(() => {
  window.removeEventListener('popstate', handlePopState);
});

/**
 * ブラウザのprev, nextボタンでのページ遷移を検知した時の処理
 */
const handlePopState = () => {
  // route.params.id の情報から取引情報の更新を行う
  updateBusiness(route.params.id as string);
};

/**
 * 取引IDを更新する
 */
const updateBusinessId = async () => {
  // 確認モーダルを表示する
  const modalResult = await openConfirmModal(
    'question',
    t('表示情報更新(詳細ページ)'),
    t('変更する(詳細ページ)'),
    t('キャンセル'),
    t('表示情報を変更しますか(詳細ページ)')
  );
  if (!modalResult.isConfirmed) return;

  const res = await diContainer.getAggregateBusinessIdUseCase.Execute(store.state.selectedInfo);
  switch (res.status.type) {
    case 'success':
      if (res.status.data == null) {
        store.state.errorType = 'API_ERROR';
        openOkInformationModal('error', t('エラー'), 'OK', t('API取得エラーメッセージ(エラーモーダル用)')); // 取引IDが取得できなかった場合はエラーモーダルを表示して何もしないで閉じる
        return;
      }

      // 取得した集計IDを用いて取引情報を更新する
      await updateBusiness(res.status.data);

      break;
    case 'error':
      console.error('更新に失敗しました。時間をおいて再度お試しください');
      store.state.errorType = 'API_ERROR';
      openOkInformationModal('error', t('エラー'), 'OK', t('API取得エラーメッセージ(エラーモーダル用)'));
      break;
  }
};

/**
 * 集計IDを基に取引情報の更新を行う
 * @param aggregateBusinessId - 集計ID
 */
const updateBusiness = async (aggregateBusinessId?: string) => {
  // 引数に集計IDが渡ってきていたら更新する
  if (aggregateBusinessId) state.selectedBusinessId = aggregateBusinessId;

  store.updateBizId(state.selectedBusinessId);

  router.push({ params: { id: state.selectedBusinessId } });

  // storeの初期化
  store.initUiState();

  store.state.loadingState = {
    status: {
      type: 'loading',
    },
  };

  const copyItems = await diContainer.getBusinessUseCase.Execute(state.selectedBusinessId as string);
  switch (copyItems.status.type) {
    case 'success':
      // 内示情報を過去表示用のstate変数に保存
      store.state.customerForecastHistory = simpleCopy(copyItems.status.data.customerAnnounce ?? []);

      // 取引情報の取得
      store.state.formSet = BusinessService.convertRecommendEditBusiness(copyItems.status.data);

      // フィルターを埋める
      store.updateSelectedInfo(
        store.state.formSet.warehouse,
        store.state.formSet.supplierName,
        store.state.formSet.businessNumber,
        store.state.formSet.businessName,
        store.state.formSet.package
      );

      // フィルターの初期値を設定
      store.state.selectedInitialInfo = simpleCopy(store.state.selectedInfo);

      // 発注済み未納を取得
      store.state.unpaidOrderedOrder = BusinessService.getUnpaidOrderedOrder(copyItems.status.data);

      store.initState();

      // inventoryがundefinedの場合(有効な当月、先月分の在庫が存在しない場合)ここで失敗として扱いエラーモーダルを表示する
      if (copyItems.status.data.inventory == null) {
        console.error('invalid inventory error');
        store.state.renderOrderFormKey += 1;
        store.state.loadingState = {
          status: {
            type: 'error',
            errorMessage: t('無効な在庫エラーメッセージ(エラーモーダル用)'),
          },
        };
        store.state.errorType = 'INVALID_INVENTORY';
        openOkInformationModal('error', t('エラー'), 'OK', undefined, t('無効な在庫エラーメッセージ(エラーモーダル用)'));
        return;
      }

      // 確定発注が存在する場合、最終確定日の翌日の日付を取得し、extraSimulateを更新する
      const lockedRecommend = store.state.recommend.filter((item) => item.isLocked || item.arrivalId != '') ?? [];
      const lockedRecommendLast = lockedRecommend?.[lockedRecommend?.length - 1];

      const startDate = store.calculateStartDate(lockedRecommendLast);
      if (startDate) {
        store.updateExtraSimulate(undefined, undefined, undefined, lockedRecommend, startDate);
      }

      store.state.renderOrderFormKey += 1;
      store.state.loadingState = {
        status: {
          type: 'success',
        },
      };
      break;
    case 'error':
      console.error('getBusinessUseCase.Execute error');
      store.state.renderOrderFormKey += 1;
      store.state.loadingState = {
        status: {
          type: 'error',
          errorMessage: t('API取得エラーメッセージ(エラーモーダル用)'),
        },
      };
      store.state.errorType = 'API_ERROR';
      openOkInformationModal('error', t('エラー'), 'OK', t('API取得エラーメッセージ(エラーモーダル用)'));
      break;
  }
};

/**
 * 集計IDを基に取引情報の更新を行う
 */
const loadAggregateBusinessIdsForPaging = () => {
  const result = diContainer.getAggregateBusinessIdsRecommendOrderListUseCase.Execute();

  switch (result.status.type) {
    case 'success':
      if (result.status.data == null) {
        console.error('getAggregateBusinessIdsRecommendOrderListUseCase.Execute null');
        return;
      }
      state.aggregateBusinessIdsHasRecommendOrder = result.status.data;
      state.recommendOrderPagingOption = {
        pageSize: state.aggregateBusinessIdsHasRecommendOrder.length,
        pageIndex: state.aggregateBusinessIdsHasRecommendOrder.indexOf(bizId as string),
      };
      break;
    case 'error':
      console.error(result.status.error);
      break;
  }
};

/**
 * ページリロードの強制実行
 * 更新処理がupdateBusinessでちゃんと出来てないので
 */
const pageReload = () => {
  store.state.loadingState.status.type = 'loading';
  location.reload();
};

provide(key, store);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(), _createBlock(OrderForm, {
          key: _unref(store).state.renderOrderFormKey,
          "table-type": "recommend-order",
          item: _unref(store).state.formSet,
          "form-set-customer-forecast-history": _unref(store).state.customerForecastHistory,
          "extra-simulate-infos": _unref(store).state.extraSimulateInfos,
          "simulation-setting": simulationSetting.value,
          onUpdateSimulate: _unref(store).updateSimulate,
          onUpdateBusinessId: updateBusinessId,
          onPagingUpdate: _cache[0] || (_cache[0] = ($event: any) => (updateBusiness($event)))
        }, null, 8, ["item", "form-set-customer-forecast-history", "extra-simulate-infos", "simulation-setting", "onUpdateSimulate"]))
      ]),
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(), _createBlock(RecommendEdit, {
          key: _unref(store).state.renderRecommendEditKey,
          "prop-extra-simulate": _unref(store).state.extraSimulateInfos,
          progress: _unref(store).state.progress,
          recommend: _unref(store).state.recommend,
          "partial-delivery-forms": _unref(store).state.partialDeliveryForms,
          "customer-announce": _unref(store).state.formSet.customerAnnounce,
          "supplier-announce": _unref(store).state.formSet.supplierAnnounce,
          "prop-checked-index": _unref(store).state.checkedIndex,
          "table-type": "recommend-order",
          item: _unref(store).state.formSet,
          "defined-order-scroll-position": _unref(store).state.recommendEditDefinedOrderScrollPosition,
          onSubmit: _unref(store).updateRecommend,
          onUpdateBusiness: pageReload,
          onSaveDefinedOrderScrollPosition: _cache[1] || (_cache[1] = 
            (posX, posY) => {
              _unref(store).state.recommendEditDefinedOrderScrollPosition = { x: posX, y: posY };
            }
          )
        }, null, 8, ["prop-extra-simulate", "progress", "recommend", "partial-delivery-forms", "customer-announce", "supplier-announce", "prop-checked-index", "item", "defined-order-scroll-position", "onSubmit"]))
      ])
    ]),
    _createVNode(InfoModal, {
      "modal-state": _unref(isLoading)(_unref(store).state.loadingState.status),
      icon: "loading",
      "enable-outside-click": false
    }, {
      title: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)('データを読み込んでいます')), 1)
      ]),
      _: 1
    }, 8, ["modal-state"]),
    _createVNode(EditUnpaidOrderedOrderModal, {
      "modal-state": _unref(store).state.isOpeningEditUnpaidOrderedOrderModal,
      "unpaid-ordered-orders": _unref(store).state.unpaidOrderedOrder,
      "execution-date": _unref(store).state.formSet.executionDate,
      onClose: _cache[2] || (_cache[2] = ($event: any) => (_unref(store).state.isOpeningEditUnpaidOrderedOrderModal = false)),
      onConfirm: _unref(store).updateUnpaidOrderedOrder
    }, null, 8, ["modal-state", "unpaid-ordered-orders", "execution-date", "onConfirm"])
  ]))
}
}

})